export const BAA_TEXT = `
# BUSINESS ASSOCIATE AGREEMENT

**Effective Date:** January 1, 2026
**Between:** City Billing, LLC ("Business Associate") AND The Registered Customer ("Covered Entity")

This Business Associate Agreement ("Agreement") is entered into by and between City Billing, LLC, a Louisiana Limited Liability Company ("Business Associate"), and the Medical Practice or Healthcare Organization registering for the FaceSheetApp service ("Covered Entity").

## 1. Catch-all Definitions
The following terms used in this Agreement shall have the same meaning as those terms in the HIPAA Rules: Breach, Data Aggregation, Designated Record Set, Disclosure, Health Care Operations, Individual, Minimum Necessary, Notice of Privacy Practices, Protected Health Information, Required By Law, Secretary, Security Incident, Subcontractor, Unsecured Protected Health Information, and Use.

## 2. Obligations and Activities of Business Associate
Business Associate agrees to:
(a) Not Use or Disclose Protected Health Information (PHI) other than as permitted or required by the Agreement or as Required by Law;
(b) Use appropriate safeguards, and comply with Subpart C of 45 CFR Part 164 with respect to electronic PHI, to prevent Use or Disclosure of PHI other than as provided for by this Agreement;
(c) Report to Covered Entity any Use or Disclosure of PHI not provided for by this Agreement of which it becomes aware, including breaches of Unsecured PHI as required at 45 CFR 164.410, and any Security Incident of which it becomes aware;
(d) In accordance with 45 CFR 164.502(e)(1)(ii) and 164.308(b)(2), if applicable, ensure that any Subcontractors that create, receive, maintain, or transmit PHI on behalf of the Business Associate agree to the same restrictions, conditions, and requirements that apply to the Business Associate with respect to such information;
(e) Make available PHI in a Designated Record Set to the Covered Entity as necessary to satisfy Covered Entity's obligations under 45 CFR 164.524;
(f) Make any amendment(s) to PHI in a Designated Record Set as directed or agreed to by the Covered Entity pursuant to 45 CFR 164.526, or take other measures as necessary to satisfy Covered Entity's obligations under 45 CFR 164.526;
(g) Maintain and make available the information required to provide an accounting of Disclosures to the Covered Entity as necessary to satisfy Covered Entity's obligations under 45 CFR 164.528;
(h) To the extent the Business Associate is to carry out one or more of Covered Entity's obligation(s) under Subpart E of 45 CFR Part 164, comply with the requirements of Subpart E that apply to the Covered Entity in the performance of such obligation(s); and
(i) Make its internal practices, books, and records available to the Secretary for purposes of determining compliance with the HIPAA Rules.

## 3. Permitted Uses and Disclosures by Business Associate
(a) Business Associate may only Use or Disclose PHI as necessary to perform the services set forth in the Service Agreement (FaceSheetApp SaaS functionality).
(b) Business Associate may Use or Disclose PHI as Required by Law.
(c) Business Associate agrees to make Uses and Disclosures and requests for PHI consistent with Covered Entity's Minimum Necessary policies and procedures.
(d) Business Associate may not Use or Disclose PHI in a manner that would violate Subpart E of 45 CFR Part 164 if done by Covered Entity.

## 4. Term and Termination
(a) **Term.** The Term of this Agreement shall be effective as of the date of registration and shall terminate when all PHI provided by Covered Entity to Business Associate, or created or received by Business Associate on behalf of Covered Entity, is destroyed or returned to Covered Entity, or, if it is infeasible to return or destroy PHI, protections are extended to such information, in accordance with the termination provisions in this Section.
(b) **Termination for Cause.** Business Associate authorizes termination of this Agreement by Covered Entity, if Covered Entity determines Business Associate has violated a material term of the Agreement and Business Associate has not cured the breach or ended the violation within the time specified by Covered Entity.

## 5. Miscellaneous
(a) **Regulatory References.** A reference in this Agreement to a section in the HIPAA Rules means the section as in effect or as amended.
(b) **Amendment.** The Parties agree to take such action as is necessary to amend this Agreement from time to time as is necessary for compliance with the requirements of the HIPAA Rules and any other applicable law.
(c) **Interpretation.** Any ambiguity in this Agreement shall be interpreted to permit compliance with the HIPAA Rules.

**IN WITNESS WHEREOF**, by checking the "I Agree" box during the FaceSheetApp registration process, the Covered Entity agrees to be bound by the terms of this Business Associate Agreement.

**City Billing, LLC**
Legal Department
Louisiana, USA
`;

export const HIPAA_POLICY_TEXT = `
# HIPAA COMPLIANCE POLICY

**Effective Date:** January 1, 2026
**Organization:** City Billing, LLC ("Company")
**Product:** FaceSheetApp

## 1. Purpose
The purpose of this policy is to ensure that City Billing, LLC complies with the Health Insurance Portability and Accountability Act of 1996 (HIPAA), the Health Information Technology for Economic and Clinical Health Act (HITECH), and the Omnibus Rule of 2013 regarding the privacy and security of Protected Health Information (PHI).

## 2. Scope
This policy applies to all workforce members, including employees, contractors, and agents of City Billing, LLC who have access to PHI.

## 3. Privacy Policy
### 3.1 Minimum Necessary Standard
City Billing, LLC shall only request, use, and disclose the minimum amount of PHI necessary to accomplish the intended purpose of the use, disclosure, or request.

### 3.2 Uses and Disclosures
PHI may only be used or disclosed for:
*   Treatment, Payment, and Health Care Operations (TPO) on behalf of our Covered Entity clients.
*   As authorized by the individual in writing.
*   As required by law.

## 4. Security Policy
### 4.1 Administrative Safeguards
*   **Security Management Process:** Company conducts annual risk assessments to identify and mitigate risks to PHI.
*   **Assigned Security Responsibility:** A designated Security Officer is responsible for the development and implementation of security policies.
*   **Workforce Training:** All employees receive annual HIPAA training.

### 4.2 Physical Safeguards
*   **Facility Access Controls:** Physical access to servers and office locations is restricted to authorized personnel.
*   **Workstation Use:** Workstations are secured with password protection and automatic logoff features.

### 4.3 Technical Safeguards
*   **Access Control:** Unique user IDs and passwords are required for all systems containing PHI. Multi-Factor Authentication (MFA) is enforced.
*   **Encryption:** All PHI is encrypted at rest (AES-256) and in transit (TLS 1.2+).
*   **Audit Controls:** Hardware, software, and procedural mechanisms are in place to record and examine activity in information systems that contain or use electronic PHI.

## 5. Breach Notification
In the event of a breach of unsecured PHI, City Billing, LLC will notify relevant Covered Entities without unreasonable delay and in no case later than 30 calendar days after strict discovery of the breach. The notification will include:
*   A brief description of what happened.
*   A description of the types of unsecured PHI that were involved.
*   Any steps individuals should take to protect themselves.
*   A brief description of what City Billing, LLC is doing to investigate the breach, to mitigate harm to individuals, and to protect against any further breaches.

## 6. Sanctions
Employees who violate this policy will be subject to disciplinary action up to and including termination of employment and potential civil/criminal penalties.

**City Billing, LLC**
Compliance Office
`;

export const TERMS_OF_SERVICE_TEXT = `
# SAAS USER TERMS OF SERVICE

**Effective Date:** January 1, 2026
**Service:** FaceSheetApp ("Service")
**Provider:** City Billing, LLC ("Company")

## 1. Acceptance of Terms
By accessing or using FaceSheetApp, you ("User") agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.

## 2. Description of Service
FaceSheetApp is a cloud-based Software-as-a-Service (SaaS) platform designed to assist healthcare providers and billing companies in extracting data from face sheets and PDF documents for data entry purposes.

## 3. Account Registration & Security
*   You must be 18 years or older to use this Service.
*   You must provide accurate and complete registration information.
*   You are responsible for maintaining the security of your account credentials.
*   You must notify Company immediately of any unauthorized use of your account.

## 4. User Obligations
*   **Compliance:** You agree to use the Service in compliance with all applicable laws, including HIPAA.
*   **PHI:** You acknowledge that you are responsible for the PHI you upload to the Service and ensure you have the necessary rights and consents.
*   **Prohibited Uses:** You may not use the Service to:
    *   Violate any laws or third-party rights.
    *   Upload malicious code or viruses.
    *   Attempt to reverse engineer or hack the Service.

## 5. Intellectual Property
City Billing, LLC retains all rights, title, and interest in and to the Service, including all related intellectual property rights. You are granted a limited, non-exclusive, non-transferable license to use the Service.

## 6. Disclaimer of Warranties
THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

## 7. Limitation of Liability
IN NO EVENT SHALL CITY BILLING, LLC BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.

## 8. Termination
We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.

## 9. Governing Law
These Terms shall be governed by and construed in accordance with the laws of the State of Louisiana, without regard to its conflict of law provisions.

## 10. Changes to Terms
We reserve the right to modify these Terms at any time. We will provide notice of significant changes. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.
`;

export const BILLING_POLICY_TEXT = `
# BILLING AND PAYMENT POLICY

**Effective Date:** January 1, 2026
**Company:** City Billing, LLC ("Company")
**Service:** FaceSheetApp ("Service")

## 1. General Principles
City Billing, LLC uses a verified payment platform (Stripe) to process all subscription and usage-based fees. By providing a payment method, you authorize us to charge the applicable fees.

## 2. Billing Authorization
By selecting a subscription plan or purchasing credits, you explicitly authorize City Billing, LLC to:
*   Charge your connected credit card or bank account for the recurring subscription fee (if applicable).
*   Charge for any usage overage fees incurred during the billing cycle.
*   Retain payment information securely via our payment processor (Stripe) for future billing.

## 3. Subscription Fees
*   **Cycle:** Subscriptions are billed monthly in advance.
*   **Renewal:** Subscriptions automatically renew unless canceled at least 24 hours before the end of the current billing period.
*   **Upgrades/Downgrades:** Plan changes take effect immediately. Prorated credits/charges may apply.

## 4. Usage-Based Fees (Overages)
*   **Calculation:** Any PDF processed beyond the plan's included allowance is charged at the "Overage Rate" specified for your plan.
*   **Timing:** Overage fees are calculated at the end of the billing cycle and charged to the payment method on file.

## 5. Refunds
*   **Subscription Fees:** Non-refundable once the billing period has commenced.
*   **Usage Fees:** Non-refundable, as the processing service has already been rendered.
*   **Exceptions:** Refunds may be issued at the sole discretion of City Billing, LLC in cases of proven technical error where the Service failed to perform as described.

## 6. Payment Failures
*   If a payment fails, we will attempt to retry the charge.
*   If payment cannot be collected within 7 days, your account will be suspended ("Locked") until payment is updated.
*   Continued non-payment may result in account termination and referral to collections.

## 7. Taxes
You are responsible for any applicable sales, use, or value-added taxes based on your billing address. All fees are exclusive of taxes unless otherwise stated.

## 8. Disputes
Any billing disputes must be reported to \`billing@citybilling.com\` within 30 days of the invoice date.
`;
